'use client'
import { CURRENCY_MAP, DEFAULT_CURRENCY } from '@partment/types'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { getLocationBasedCurrency } from '../api/locationCurrency'
import { getExchangeRates } from '../api/rates'
import { storage } from '../utils/CurrencySelector/ExchangeStorage'

interface CurrencyContextType {
  currency: any
  changeCurrency: (newCurrency: any) => void
  isHydrated: boolean
  ratesLoaded: boolean
}

const CurrencyContext = createContext<CurrencyContextType | null>(null)

export const useCurrency = () => {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize with stored preference or default immediately to avoid lag
  const [currency, setCurrency] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedPrefs = storage.getUserPrefs()
      return storedPrefs.currency || DEFAULT_CURRENCY
    }
    return DEFAULT_CURRENCY
  })
  const [isHydrated, setIsHydrated] = useState(false)
  const [ratesLoaded, setRatesLoaded] = useState(false)

  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        // Load exchange rates in parallel with location detection
        const ratesPromise = getExchangeRates()

        // Check if user has explicitly saved preferences
        if (storage.hasStoredPrefs()) {
          // User has saved preference, use it and we're done
          await ratesPromise // Wait for rates to complete
          setRatesLoaded(true)
          setIsHydrated(true)
          return
        }

        // No saved preference, detect location-based currency
        const { currency: detectedCurrency, detected } =
          await getLocationBasedCurrency()

        // Update currency with detected value
        setCurrency(detectedCurrency)

        // Only save as user preference if detection actually succeeded
        // This prevents saving USD when detection failed due to network issues
        if (detected) {
          const newSymbol =
            detectedCurrency in CURRENCY_MAP
              ? CURRENCY_MAP[detectedCurrency as keyof typeof CURRENCY_MAP]
              : '$'
          storage.setUserPrefs({
            currency: detectedCurrency,
            currency_symbol: newSymbol,
          })
        }

        // Ensure rates are loaded
        await ratesPromise
        setRatesLoaded(true)
      } catch (error) {
        console.warn('Currency initialization failed:', error)
        // Keep current currency on error
      } finally {
        setIsHydrated(true)
      }
    }

    // Defer network calls to avoid blocking hydration
    const id = setTimeout(initializeCurrency, 0)
    return () => clearTimeout(id)
  }, [])

  const changeCurrency = (newCurrency: any) => {
    const newSymbol =
      newCurrency in CURRENCY_MAP
        ? CURRENCY_MAP[newCurrency as keyof typeof CURRENCY_MAP]
        : '$'
    setCurrency(newCurrency)

    // Update storage
    storage.setUserPrefs({
      currency: newCurrency,
      currency_symbol: newSymbol,
    })
  }

  const value = {
    currency,
    changeCurrency,
    isHydrated,
    ratesLoaded,
  }

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  )
}
